import type { HotkeyConfig, KeyModifier, NormalizedKeyEvent } from "../types";
import { EventEmitter } from "./EventEmitter";

export interface Command {
  id: string;
  title: string;
  description?: string;
  category?: string;
  hotkey?: {
    key: string;
    modifiers?: KeyModifier[];
  };
  handler: () => void | Promise<void>;
  enabled?: boolean;
  keywords?: string[];
  icon?: string | unknown;
  priority?: number;
}

export const COMMAND_EVENTS = {
  REGISTERED: "command:registered",
  UNREGISTERED: "command:unregistered",
  EXECUTED: "command:executed",
  UPDATED: "command:updated",
} as const;

export class CommandRegistry extends EventEmitter {
  private static instance: CommandRegistry | null = null;
  private commands = new Map<string, Command>();

  private constructor() {
    super();
  }

  static getInstance(): CommandRegistry {
    if (!CommandRegistry.instance) {
      CommandRegistry.instance = new CommandRegistry();
    }
    return CommandRegistry.instance;
  }

  register(command: Command): string {
    const id = command.id;

    if (this.commands.has(id)) {
      console.warn(`command "${id}" is already registered. updating...`);
      this.update(id, command);
      return id;
    }

    this.commands.set(id, {
      enabled: true,
      priority: 50,
      ...command,
    });

    this.emit(COMMAND_EVENTS.REGISTERED, command);

    return id;
  }

  registerFromHotkey(hotkeyId: string, config: HotkeyConfig): string {
    const command: Command = {
      id: hotkeyId,
      title: config.description,
      description: config.description,
      category: config.category,
      hotkey: {
        key: config.key,
        modifiers: config.modifiers,
      },
      handler: () => {
        const event = { key: config.key, modifiers: new Set(config.modifiers || []) };
        config.handler(event as NormalizedKeyEvent);
      },
      priority: config.priority,
    };

    return this.register(command);
  }

  unregister(id: string): boolean {
    const command = this.commands.get(id);
    if (!command) return false;

    this.commands.delete(id);
    this.emit(COMMAND_EVENTS.UNREGISTERED, id);

    return true;
  }

  update(id: string, updates: Partial<Command>): boolean {
    const command = this.commands.get(id);
    if (!command) return false;

    const updated = { ...command, ...updates };
    this.commands.set(id, updated);
    this.emit(COMMAND_EVENTS.UPDATED, updated);

    return true;
  }

  get(id: string): Command | undefined {
    return this.commands.get(id);
  }

  getAll(): Command[] {
    return Array.from(this.commands.values()).sort((a, b) => {
      const priorityDiff = (b.priority || 50) - (a.priority || 50);
      if (priorityDiff !== 0) return priorityDiff;
      return a.title.localeCompare(b.title);
    });
  }

  getByCategory(category: string): Command[] {
    return this.getAll().filter((cmd) => cmd.category === category);
  }

  getAllCategories(): string[] {
    const categories = new Set<string>();
    this.commands.forEach((cmd) => {
      if (cmd.category) {
        categories.add(cmd.category);
      }
    });
    return Array.from(categories).sort();
  }

  search(query: string): Command[] {
    const lowerQuery = query.toLowerCase();

    return this.getAll().filter((cmd) => {
      if (!cmd.enabled) return false;

      if (cmd.title.toLowerCase().includes(lowerQuery)) return true;
      if (cmd.description?.toLowerCase().includes(lowerQuery)) return true;
      if (cmd.category?.toLowerCase().includes(lowerQuery)) return true;
      if (cmd.keywords?.some((keyword) => keyword.toLowerCase().includes(lowerQuery))) return true;

      return false;
    });
  }

  async execute(id: string): Promise<boolean> {
    const command = this.commands.get(id);
    if (!command || !command.enabled) return false;

    try {
      await command.handler();
      this.emit(COMMAND_EVENTS.EXECUTED, command);
      return true;
    } catch (error) {
      console.error(`error executing command "${id}":`, error);
      return false;
    }
  }

  clear(): void {
    this.commands.clear();
  }

  size(): number {
    return this.commands.size;
  }
}

export function getCommandRegistry(): CommandRegistry {
  return CommandRegistry.getInstance();
}

export function registerCommand(command: Command): string {
  return getCommandRegistry().register(command);
}

export function unregisterCommand(id: string): boolean {
  return getCommandRegistry().unregister(id);
}

export function executeCommand(id: string): Promise<boolean> {
  return getCommandRegistry().execute(id);
}
